'use client'

import { setCookie } from 'cookies-next'
import { useParams, useRouter } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

import { Box, ListIcon, PurchaseIcon, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@hermes/web-components'

import useTranslateMessage from '@hooks/useTranslateMessage'
import { useUserToken } from '@hooks/useUserToken'

import AddToWishlist from '@components/AddToWishlistModal'
import keyWithCookieSuffix from '@lib/utils/keyWithCookieSuffix'

import { ExtendedBook } from '@hermes/types'
import { useLocale } from '@hooks/useLocale'
import { BuyerActionButton, BuyerActionsContainer } from './ActionSection.styles'

interface SecondarySectionProps {
  navigationRefs: { [key: string]: React.RefObject<HTMLDivElement> }
  bookDetails: ExtendedBook
}

const SecondarySection = ({ navigationRefs, bookDetails }: SecondarySectionProps) => {
  const { t } = useTranslateMessage()
  const router = useRouter()
  const locale = useLocale()
  const slug = useParams()?.slug as string
  const { token } = useUserToken()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const navigationsRef = useRef<HTMLDivElement>()

  const [tabValue, setTabValue] = useState('about')
  const [showNavigation, setShowNavigation] = useState(false)
  const [addToWishListModalView, setAddToWishListModalView] = useState(false)

  const buyerActions = [
    {
      label: t({ id: 'bdp.buyerActions.addToWishlist' }),
      action: async () => {
        if (token) setAddToWishListModalView(true)
        else await router.push(`/${locale}/auth`)
      },
      icon: <ListIcon width="20px" />
    },
    {
      label: t({ id: 'bdp.buyerActions.sellBook' }),
      action: async () => {
        setCookie(keyWithCookieSuffix('saleBookId', locale), bookDetails.id)
        await router.push(`/${locale}/add-sale?nextStep=correct`)
      },
      icon: <PurchaseIcon width="20px" />
    }
  ]

  const scrolledToActions = () => {
    setShowNavigation(Boolean(navigationsRef.current && window.scrollY >= navigationsRef.current.offsetTop))
  }

  const scrollToRef = (value: string) => {
    const refKey = value as keyof typeof navigationRefs
    const ref = navigationRefs[refKey]
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', () => {
      const { scrollY } = window
      Object.keys(navigationRefs).forEach((key: string) => {
        const refKey = key as keyof typeof navigationRefs
        const ref = navigationRefs[refKey]

        if (ref.current && scrollY >= ref.current.offsetTop - 100) {
          setTabValue(key)
        }
      })

      scrolledToActions()
    })

    return () => {
      window.removeEventListener('scroll', () => null)
    }
  }, [token, slug])

  const Navigation = () => (
    <Box position="sticky" top="12px">
      <Typography pt="18px" mb="16px" pl="12px" fontSize="16px" fontWeight={600}>
        {t({ id: 'bdp.navigationTitle' })}:
      </Typography>
      <Tabs orientation="vertical" value={tabValue} onChange={(e, value) => scrollToRef(value)}>
        <Tab label={t({ id: 'bdp.aboutBook' })} value="about" />
        {Boolean(bookDetails.versions?.length) && <Tab label={t({ id: 'versions' })} value="versions" />}
        <Tab label={t({ id: 'bdp.reviews' })} value="reviews" />
        <Tab label={t({ id: 'bdp.mightLike' })} value="mightLike" />
      </Tabs>
    </Box>
  )

  return (
    <Box height="100%">
      <Box height="100%" ref={navigationsRef}>
        {!isMobile && showNavigation ? (
          <Navigation />
        ) : (
          <BuyerActionsContainer>
            {buyerActions.map(
              (action, index) =>
                action && (
                  <BuyerActionButton variant="ghost" onClick={action.action} key={index}>
                    {action.icon}
                    {action.label}
                  </BuyerActionButton>
                )
            )}
          </BuyerActionsContainer>
        )}
      </Box>
      {token && (
        <AddToWishlist
          bookId={bookDetails.id}
          isOpen={addToWishListModalView}
          onClose={() => setAddToWishListModalView(false)}
        />
      )}
    </Box>
  )
}

export default SecondarySection
